// Export für den Steuerberater: Zeitraum und Format selbst wählen.
//
// Früher gab es nur die DATEV-Datei (CSV). Auf vielen Rechnern öffnet sich
// eine .csv in einem Texteditor — für den Inhaber unlesbar. Jetzt:
//   PDF   zum Lesen, Ablegen, Mailen
//   DATEV zum Einlesen in die Kanzleisoftware
//   XML   dieselben Daten strukturiert für andere Programme
import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { useState } from 'react';
import { token } from '../../../gemeinsam/lib/api';
import { useDaten, euro } from '../../../gemeinsam/lib/daten';
import { Auswahl, Hinweis, Karte, KartenKopf, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Vorschau = {
  zeitraum: { von: string; bis: string; jahr: number };
  anzahlBuchungen: number; summe: number; kontenrahmen: string; format: string; hinweis: string;
};

const FORMATE = [
  { id: 'pdf', titel: 'PDF', text: 'Bericht zum Lesen und Ablegen: Übersicht, Rechnungsausgangsbuch, Ausgabenbuch, Buchungsliste.' },
  { id: 'datev', titel: 'DATEV (CSV)', text: 'Buchungsstapel, den die Kanzlei direkt in DATEV einliest. SKR03, Erlöse je Steuersatz getrennt.' },
  { id: 'xml', titel: 'XML', text: 'Dieselben Daten strukturiert — für andere Buchhaltungsprogramme.' },
] as const;

const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export default function SteuerberaterExport() {
  const heute = new Date();
  const [jahr, setJahr] = useState(heute.getFullYear());
  // '' = ganzes Jahr, 'q1'…'q4' = Quartal, '1'…'12' = Monat
  const [zeitraum, setZeitraum] = useState('');
  const [format, setFormat] = useState<'pdf' | 'datev' | 'xml'>('pdf');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const query = `jahr=${jahr}${zeitraum.startsWith('q') ? `&quartal=${zeitraum.slice(1)}` : zeitraum ? `&monat=${zeitraum}` : ''}`;
  const { daten: vorschau } = useDaten<Vorschau>(`/datev/vorschau?${query}`, [query]);

  async function herunterladen() {
    setLaedt(true); setFehler(null);
    try {
      const antwort = await fetch(apiUrl(`/api/datev/export?${query}&format=${format}`), {
        headers: { Authorization: `Bearer ${token() ?? ''}` },
      });
      if (!antwort.ok) throw new Error(String(antwort.status));
      // Dateiname vom Server — mit Firma und Zeitraum.
      const kopf = antwort.headers.get('content-disposition') || '';
      const name = decodeURIComponent(/filename\*=UTF-8''([^;]+)/.exec(kopf)?.[1] || /filename="([^"]+)"/.exec(kopf)?.[1]
        || `Steuerberater_${jahr}.${format === 'datev' ? 'csv' : format}`);
      const url = URL.createObjectURL(await antwort.blob());
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      setFehler('Der Export konnte nicht erstellt werden. Bitte erneut versuchen.');
    } finally { setLaedt(false); }
  }

  return (
    <Karte>
      <KartenKopf titel="Export für den Steuerberater" hinweis="Zeitraum und Format wählen" />
      <div className="space-y-4 p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <Auswahl label="Jahr" value={String(jahr)} onChange={e => setJahr(Number(e.target.value))}>
            {[0, 1, 2, 3].map(n => <option key={n} value={heute.getFullYear() - n}>{heute.getFullYear() - n}</option>)}
          </Auswahl>
          <Auswahl label="Zeitraum" value={zeitraum} onChange={e => setZeitraum(e.target.value)}>
            <option value="">Ganzes Jahr</option>
            <optgroup label="Quartal">
              {[1, 2, 3, 4].map(q => <option key={q} value={`q${q}`}>{q}. Quartal</option>)}
            </optgroup>
            <optgroup label="Monat">
              {MONATE.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
            </optgroup>
          </Auswahl>
        </div>

        <fieldset>
          <legend className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">Format</legend>
          <div className="grid gap-2 sm:grid-cols-3">
            {FORMATE.map(f => (
              <label key={f.id}
                     className={`cursor-pointer rounded-lg border p-3 text-sm transition ${format === f.id
                       ? 'border-marke-500 bg-marke-50 ring-1 ring-marke-500 dark:bg-marke-950/40'
                       : 'border-slate-200 hover:border-slate-300 dark:border-slate-700'}`}>
                <input type="radio" name="exportformat" value={f.id} checked={format === f.id}
                       onChange={() => setFormat(f.id)} className="sr-only" />
                <span className="block font-semibold text-slate-900 dark:text-slate-100">{f.titel}</span>
                <span className="mt-1 block text-xs text-slate-600 dark:text-slate-400">{f.text}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {!vorschau ? <Spinner klein /> : (
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {new Date(vorschau.zeitraum.von).toLocaleDateString('de-DE')} – {new Date(vorschau.zeitraum.bis).toLocaleDateString('de-DE')}
            {' · '}{vorschau.anzahlBuchungen} Buchungen · {euro(vorschau.summe)} Umsatz in Buchungen
          </p>
        )}

        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
        <Knopf variante="primaer" laedt={laedt} onClick={herunterladen}>
          {FORMATE.find(f => f.id === format)?.titel} herunterladen
        </Knopf>

        <Hinweis art="info" titel="Welches Format?">
          Für die Kanzlei meist <strong>DATEV</strong> — das liest die Software direkt ein. Das <strong>PDF</strong> ist
          zum Durchsehen und Ablegen gedacht und zeigt dieselben Zahlen. Gutschriften mindern Umsatz und
          Umsatzsteuer im Monat ihrer Ausstellung. {vorschau?.hinweis}
        </Hinweis>
      </div>
    </Karte>
  );
}
